"use client";

import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { v4 as uuidv4 } from "uuid";
import {
  Users,
  Plus,
  ChevronDown,
  X,
  Filter,
  Sparkles,
  Zap,
  Calendar,
  Check,
  CalendarDays,
} from "lucide-react";
import { cn } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AudienceCriteria,
  AudienceFilter,
} from "../../types/audienceTypes";
import { getFieldById } from "../../config/filterFields";
import FilterCategoryModal from "../FilterCategorySelector/FilterCategoryModal";
import FilterValueEditor from "../FilterValueEditor/FilterValueEditor";
import { EntitySingleSelect } from "@/components/EntitySingleSelect/EntitySingleSelect";
import { formatDate } from "@/utils/dateUtils";
import { FetchEventsLookupResponse } from "@/modules/emailManagement/features/EmailCampaignManagement/api/fetchEventsLookup/types";
import { fetchEventsLookup } from "@/modules/emailManagement/features/EmailCampaignManagement/api/fetchEventsLookup/fetchEventsLookupApi";
import { FetchEventSessionsLookupResponse } from "@/modules/emailManagement/features/EmailCampaignManagement/api/fetchEventSessionsLookup/types";
import { fetchEventSessionsLookup } from "@/modules/emailManagement/features/EmailCampaignManagement/api/fetchEventSessionsLookup/fetchEventSessionsLookupApi";

// ============================================================================
// Types
// ============================================================================

interface EntityOption {
  id: number;
  name: string;
}

interface AudienceTargetingValue {
  event: EntityOption | null;
  session: EntityOption | null;
  audienceCriteria: AudienceCriteria | null;
}

interface AudienceTargetingCardProps {
  value: AudienceTargetingValue;
  onChange: (value: AudienceTargetingValue) => void;
  recipientCount?: number;
  isLoadingCount?: boolean;
}

// ============================================================================
// Inline Styles
// ============================================================================

const inlineStyles = `
  .audience-targeting-card {
    --atc-bg: #ffffff;
    --atc-bg-secondary: #f8fafc;
    --atc-bg-tertiary: #f1f5f9;
    --atc-border: #e2e8f0;
    --atc-border-hover: #cbd5e1;
    --atc-text: #0f172a;
    --atc-text-secondary: #64748b;
    --atc-text-muted: #94a3b8;
    --atc-accent: #f97316;
    --atc-accent-light: #fff7ed;
    --atc-success: #22c55e;
  }

  .atc-card {
    background: var(--atc-bg);
    border: 1px solid var(--atc-border);
    border-radius: 12px;
    overflow: hidden;
  }

  .atc-section {
    padding: 16px;
    border-bottom: 1px solid var(--atc-border);
  }

  .atc-section:last-child {
    border-bottom: none;
  }

  .atc-section-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
  }

  .atc-section-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .atc-section-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--atc-text);
  }

  .atc-section-subtitle {
    font-size: 12px;
    color: var(--atc-text-secondary);
  }

  .atc-filter-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: var(--atc-bg-secondary);
    border: 1px solid var(--atc-border);
    border-radius: 8px;
    font-size: 13px;
    color: var(--atc-text);
    transition: all 0.2s ease;
  }

  .atc-filter-tag:hover {
    border-color: var(--atc-accent);
    background: var(--atc-accent-light);
  }

  .atc-logic-badge {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.05em;
    padding: 2px 8px;
    border-radius: 4px;
    background: #f3e8ff;
    color: #7c3aed;
  }

  .atc-selected-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: var(--atc-bg-secondary);
    border: 1px solid var(--atc-border);
    border-radius: 8px;
    font-size: 13px;
    color: var(--atc-text);
  }

  .atc-selected-tag.active {
    background: var(--atc-accent-light);
    border-color: var(--atc-accent);
  }
`;

// ============================================================================
// Helper Functions
// ============================================================================

function formatFilterDisplay(filter: AudienceFilter): string {
  const field = getFieldById(filter.fieldId);
  if (!field) return filter.fieldId;

  let valueDisplay = "";

  if (filter.value === null || filter.value === undefined) {
    valueDisplay = "...";
  } else if (Array.isArray(filter.value)) {
    const valueArray = filter.value as (string | number)[];
    if (valueArray.length === 0) {
      valueDisplay = "...";
    } else if (valueArray.length === 1) {
      const option = field.options?.find((o) => String(o.id) === String(valueArray[0]));
      valueDisplay = option?.label || String(valueArray[0]);
    } else {
      valueDisplay = `${valueArray.length} selected`;
    }
  } else if (typeof filter.value === "object" && "from" in filter.value) {
    valueDisplay = `${filter.value.from} - ${filter.value.to}`;
  } else if (typeof filter.value === "boolean") {
    valueDisplay = filter.value ? "Yes" : "No";
  } else {
    const option = field.options?.find((o) => String(o.id) === String(filter.value));
    valueDisplay = option?.label || String(filter.value);
  }

  return `${field.label}: ${valueDisplay}`;
}

// ============================================================================
// Main Component
// ============================================================================

export function AudienceTargetingCard({
  value,
  onChange,
  recipientCount,
  isLoadingCount = false,
}: AudienceTargetingCardProps) {
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const { event, session, audienceCriteria } = value;

  // Calculate total filters
  const totalFilters = audienceCriteria?.filterGroups.reduce(
    (acc, group) => acc + group.filters.length,
    0
  ) || 0;

  // Handle event change
  const handleEventChange = useCallback((newEvent: EntityOption | null) => {
    onChange({
      event: newEvent,
      session: null, // Reset session when event changes
      audienceCriteria: value.audienceCriteria,
    });
  }, [onChange, value.audienceCriteria]);

  // Handle session change
  const handleSessionChange = useCallback((newSession: EntityOption | null) => {
    onChange({
      ...value,
      session: newSession,
    });
  }, [onChange, value]);

  // Handle adding a new filter
  const handleAddFilter = useCallback(
    (fieldId: string) => {
      const newFilter: AudienceFilter = {
        id: uuidv4(),
        fieldId,
        operator: "is_any_of",
        value: null,
      };

      let newCriteria: AudienceCriteria;

      if (!audienceCriteria || audienceCriteria.filterGroups.length === 0) {
        newCriteria = {
          type: "active",
          groupLogic: "AND",
          filterGroups: [
            {
              id: uuidv4(),
              logic: "AND",
              filters: [newFilter],
            },
          ],
        };
      } else {
        const updatedGroups = [...audienceCriteria.filterGroups];
        const lastGroup = { ...updatedGroups[updatedGroups.length - 1] };
        lastGroup.filters = [...lastGroup.filters, newFilter];
        updatedGroups[updatedGroups.length - 1] = lastGroup;

        newCriteria = {
          ...audienceCriteria,
          filterGroups: updatedGroups,
        };
      }

      onChange({
        ...value,
        audienceCriteria: newCriteria,
      });

      setIsFilterModalOpen(false);
      setIsFiltersExpanded(true);
    },
    [audienceCriteria, onChange, value]
  );

  // Handle removing a filter
  const handleRemoveFilter = useCallback(
    (groupId: string, filterId: string) => {
      if (!audienceCriteria) return;

      const updatedGroups = audienceCriteria.filterGroups
        .map((group) => {
          if (group.id !== groupId) return group;
          return {
            ...group,
            filters: group.filters.filter((f) => f.id !== filterId),
          };
        })
        .filter((group) => group.filters.length > 0);

      onChange({
        ...value,
        audienceCriteria: updatedGroups.length === 0 ? null : {
          ...audienceCriteria,
          filterGroups: updatedGroups,
        },
      });
    },
    [audienceCriteria, onChange, value]
  );

  // Handle updating a filter (operator, value)
  const handleUpdateFilter = useCallback(
    (groupId: string, filterId: string, updates: Partial<AudienceFilter>) => {
      if (!audienceCriteria) return;

      const updatedGroups = audienceCriteria.filterGroups.map((group) => {
        if (group.id !== groupId) return group;
        return {
          ...group,
          filters: group.filters.map((f) =>
            f.id === filterId ? { ...f, ...updates } : f
          ),
        };
      });

      onChange({
        ...value,
        audienceCriteria: {
          ...audienceCriteria,
          filterGroups: updatedGroups,
        },
      });
    },
    [audienceCriteria, onChange, value]
  );

  // Toggle group logic
  const handleToggleGroupLogic = useCallback(() => {
    if (!audienceCriteria) return;
    onChange({
      ...value,
      audienceCriteria: {
        ...audienceCriteria,
        groupLogic: audienceCriteria.groupLogic === "AND" ? "OR" : "AND",
      },
    });
  }, [audienceCriteria, onChange, value]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    onChange({
      ...value,
      audienceCriteria: null,
    });
  }, [onChange, value]);

  return (
    <>
      <style>{inlineStyles}</style>

      <div className="audience-targeting-card">
        <div className="atc-card">
          {/* Header with recipient count */}
          <div className="px-4 py-3 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-sm">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Audience Targeting</h3>
                  <p className="text-xs text-gray-600">
                    {!event ? (
                      "Select an event and session to target attendees"
                    ) : !session ? (
                      "Select a session to continue"
                    ) : isLoadingCount ? (
                      <span className="flex items-center gap-1 text-orange-600">
                        <Zap className="w-3 h-3 animate-pulse" />
                        Calculating recipients...
                      </span>
                    ) : recipientCount !== undefined ? (
                      <span className="text-orange-600 font-medium">
                        {recipientCount.toLocaleString()} recipients
                        {totalFilters > 0 && ` (${totalFilters} filters applied)`}
                      </span>
                    ) : (
                      "Ready to add filters"
                    )}
                  </p>
                </div>
              </div>
              {session && recipientCount !== undefined && (
                <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                  {recipientCount.toLocaleString()}
                </Badge>
              )}
            </div>
          </div>

          {/* Event Selection */}
          <div className="atc-section">
            <div className="atc-section-header">
              <div className="atc-section-icon bg-blue-100">
                <Calendar className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <div className="atc-section-title">Event</div>
                <div className="atc-section-subtitle">Select the event for your campaign</div>
              </div>
            </div>
            <EntitySingleSelect
              entityNamePlural="Events"
              entityNameSingular="Event"
              fetchEntities={async ({ searchTerm, page, pageSize }) => {
                const response: FetchEventsLookupResponse = await fetchEventsLookup({
                  searchTerm,
                  page,
                  pageSize,
                  sortBy: "eventName",
                  sortOrder: "asc",
                });
                return {
                  data: response.data.map((ev) => ({
                    id: ev.id,
                    name: ev.name,
                  })),
                  totalCount: response.meta?.totalCount ?? response.data.length,
                };
              }}
              onChange={handleEventChange}
              renderEntityRow={(ent, isSelected, toggle) => (
                <div
                  className={cn(
                    "cursor-pointer flex items-center justify-between py-3 px-3 border-b last:border-0 transition-colors",
                    isSelected ? "bg-orange-50" : "hover:bg-gray-50"
                  )}
                  key={ent.id}
                  onClick={() => toggle(ent)}
                >
                  <span className="text-sm text-gray-900">{ent.name}</span>
                  {isSelected && <Check className="h-4 w-4 text-orange-600" />}
                </div>
              )}
              value={event}
            />
          </div>

          {/* Session Selection */}
          <div className="atc-section">
            <div className="atc-section-header">
              <div className="atc-section-icon bg-purple-100">
                <CalendarDays className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <div className="atc-section-title">Session</div>
                <div className="atc-section-subtitle">Choose the specific session date</div>
              </div>
            </div>
            <EntitySingleSelect
              disabled={!event}
              disabledMessage="Select an event first"
              entityNamePlural="Sessions"
              entityNameSingular="Session"
              fetchEntities={async ({ searchTerm, page, pageSize }) => {
                if (!event?.id) return { data: [], totalCount: 0 };

                const response: FetchEventSessionsLookupResponse = await fetchEventSessionsLookup({
                  eventId: event.id,
                  page,
                  pageSize,
                  sortBy: "startDate",
                  sortOrder: "asc",
                  searchTerm,
                });
                return {
                  data: response.data.map((es) => ({
                    id: es.id,
                    name: formatDate(es.startDate),
                  })),
                  totalCount: response.meta?.totalCount ?? response.data.length,
                };
              }}
              onChange={handleSessionChange}
              renderEntityRow={(ent, isSelected, toggle) => (
                <div
                  className={cn(
                    "cursor-pointer flex items-center justify-between py-3 px-3 border-b last:border-0 transition-colors",
                    isSelected ? "bg-orange-50" : "hover:bg-gray-50"
                  )}
                  key={ent.id}
                  onClick={() => toggle(ent)}
                >
                  <span className="text-sm text-gray-900">
                    <b>Start Date:</b> {ent.name}
                  </span>
                  {isSelected && <Check className="h-4 w-4 text-orange-600" />}
                </div>
              )}
              value={session}
            />
          </div>

          {/* Additional Filters Section */}
          <div className="atc-section bg-gray-50">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => session && setIsFiltersExpanded(!isFiltersExpanded)}
            >
              <div className="atc-section-header mb-0">
                <div className="atc-section-icon bg-green-100">
                  <Filter className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="atc-section-title">Additional Filters</span>
                    {totalFilters > 0 && (
                      <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                        {totalFilters}
                      </Badge>
                    )}
                    <span className="text-xs text-gray-400">(Optional)</span>
                  </div>
                  <div className="atc-section-subtitle">
                    {!session
                      ? "Select a session first to add filters"
                      : totalFilters === 0
                        ? "Refine your audience with additional criteria"
                        : `${totalFilters} filter${totalFilters !== 1 ? 's' : ''} applied`
                    }
                  </div>
                </div>
              </div>
              {session && (
                <ChevronDown
                  className={cn(
                    "w-5 h-5 text-gray-400 transition-transform",
                    isFiltersExpanded && "rotate-180"
                  )}
                />
              )}
            </div>

            {/* @ts-ignore - framer-motion type incompatibility with React 18 */}
            <AnimatePresence>
              {isFiltersExpanded && session && (
                <motion.div
                  animate={{ height: "auto", opacity: 1 }}
                  className="overflow-hidden"
                  exit={{ height: 0, opacity: 0 }}
                  initial={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="pt-4">
                    {/* Empty State */}
                    {totalFilters === 0 && (
                      <div className="text-center py-6 bg-white rounded-lg border border-dashed border-gray-300">
                        <Sparkles className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-500 mb-3">
                          No filters applied - targeting all session attendees
                        </p>
                        <Button
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => setIsFilterModalOpen(true)}
                          size="sm"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Filter
                        </Button>
                      </div>
                    )}

                    {/* Filter Tags */}
                    {totalFilters > 0 && audienceCriteria && (
                      <div className="space-y-3 bg-white rounded-lg border border-gray-200 p-3">
                        {audienceCriteria.filterGroups.map((group, groupIndex) => (
                          <React.Fragment key={group.id}>
                            {groupIndex > 0 && (
                              <div className="flex items-center justify-center py-1">
                                <button
                                  className="atc-logic-badge cursor-pointer hover:opacity-80"
                                  onClick={handleToggleGroupLogic}
                                >
                                  {audienceCriteria.groupLogic}
                                </button>
                              </div>
                            )}

                            <div className="flex flex-wrap items-center gap-2">
                              {group.filters.map((filter, filterIndex) => (
                                <React.Fragment key={filter.id}>
                                  {filterIndex > 0 && (
                                    <span className="atc-logic-badge">
                                      {group.logic}
                                    </span>
                                  )}
                                  <FilterValueEditor
                                    filter={filter}
                                    onRemove={() => handleRemoveFilter(group.id, filter.id)}
                                    onUpdate={(updates) => handleUpdateFilter(group.id, filter.id, updates)}
                                  >
                                    <div className="atc-filter-tag group cursor-pointer">
                                      <Filter className="w-3 h-3 text-green-600" />
                                      <span className="max-w-48 truncate">
                                        {formatFilterDisplay(filter)}
                                      </span>
                                      <button
                                        className="p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveFilter(group.id, filter.id);
                                        }}
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </FilterValueEditor>
                                </React.Fragment>
                              ))}
                            </div>
                          </React.Fragment>
                        ))}

                        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                          <Button
                            className="text-gray-600 border-dashed"
                            onClick={() => setIsFilterModalOpen(true)}
                            size="sm"
                            variant="outline"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Filter
                          </Button>
                          <Button
                            className="text-gray-500"
                            onClick={handleClearFilters}
                            size="sm"
                            variant="ghost"
                          >
                            Clear All
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      <FilterCategoryModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onSelectField={handleAddFilter}
      />
    </>
  );
}

export default AudienceTargetingCard;
